import { useCallback, useEffect, useState } from "react";
import {
  fetchOperators,
  fetchPurgePlan,
  grantOperator,
  purgeTestData,
  revokeOperator,
} from "../lib/api";
import { Mail, ShieldCheck, Trash2, UserPlus, X } from "lucide-react";

/** Grant a colleague operator access by email. Held as an invite until they first sign in. */
export const Operators = () => {
  const [d, setD] = useState(null);
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => fetchOperators().then(setD).catch(() => {}), []);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (fn, note) => {
    setBusy(true);
    setMsg("");
    try {
      const res = await fn();
      await load();
      setMsg(typeof note === "function" ? note(res) : note);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "that did not take");
    } finally {
      setBusy(false);
    }
  };

  const add = () =>
    run(
      () => grantOperator(email.trim()),
      (res) => {
        setEmail("");
        return res.status === "invited"
          ? `${res.email} invited — they become an operator on their first Google sign-in`
          : res.status === "promoted"
            ? `${res.email} promoted now (already had an account)`
            : `${res.email} was already an operator`;
      }
    );

  return (
    <div data-testid="admin-operators" className="space-y-10">
      <div>
        <p className="label">Operator access</p>
        <p className="mt-2 max-w-2xl text-xs leading-relaxed text-zinc-500">
          An operator can read every subject's answers, telemetry and map, and can drive the
          instrument. Grant it only to people who should see that. This is the alternative to
          handing out the passcode — which grants the same power to whoever types it.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <input
          data-testid="operator-email-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && email.trim()) add();
          }}
          placeholder="colleague@company.com"
          className="min-w-[18rem] flex-1 border border-[var(--line-hi)] bg-[#0a0a0d] px-4 py-3 font-data text-[12px] text-zinc-100 outline-none placeholder:text-zinc-700"
        />
        <button
          data-testid="operator-add-btn"
          disabled={busy || !email.trim()}
          onClick={add}
          className="btn-stark flex items-center gap-2"
        >
          <UserPlus size={11} strokeWidth={1.8} /> Grant access
        </button>
      </div>

      <div>
        <p className="label">Operators · {d?.operators?.length ?? 0}</p>
        <ul className="mt-4" data-testid="operator-list">
          {(d?.operators || []).map((o) => (
            <li
              key={o.user_id}
              data-testid={`operator-row-${o.email}`}
              className="flex flex-wrap items-center gap-3 border-b border-[var(--line)] py-3.5"
            >
              <ShieldCheck size={12} strokeWidth={1.7} className="text-zinc-500" />
              <span className="text-sm text-zinc-100">{o.name || "—"}</span>
              <span className="font-data text-[11px] text-zinc-500">{o.email}</span>
              {o.is_you && <span className="label !text-zinc-300">you</span>}
              {o.from_env && <span className="label">from environment</span>}
              {!o.is_you && !o.from_env && (
                <button
                  data-testid={`operator-revoke-${o.email}`}
                  disabled={busy}
                  onClick={() => run(() => revokeOperator(o.email), `${o.email} revoked`)}
                  className="label ml-auto flex items-center gap-1.5 hover:text-zinc-100"
                >
                  <X size={11} strokeWidth={1.8} /> revoke
                </button>
              )}
            </li>
          ))}
          {(d?.operators || []).length === 0 && (
            <p className="font-data text-[11px] text-zinc-600">
              No account-based operators — only passcode access.
            </p>
          )}
        </ul>
      </div>

      <div>
        <p className="label">Pending invites · {d?.invites?.length ?? 0}</p>
        <ul className="mt-4" data-testid="operator-invite-list">
          {(d?.invites || []).map((i) => (
            <li
              key={i.email}
              data-testid={`operator-invite-${i.email}`}
              className="flex flex-wrap items-center gap-3 border-b border-[var(--line)] py-3.5"
            >
              <Mail size={12} strokeWidth={1.7} className="text-zinc-600" />
              <span className="font-data text-[11px] text-zinc-300">{i.email}</span>
              <span className="font-data text-[10px] text-zinc-600">
                invited by {i.invited_by} · {i.invited_at?.slice(0, 16).replace("T", " ")}
              </span>
              <button
                data-testid={`operator-withdraw-${i.email}`}
                disabled={busy}
                onClick={() => run(() => revokeOperator(i.email), `invite to ${i.email} withdrawn`)}
                className="label ml-auto flex items-center gap-1.5 hover:text-zinc-100"
              >
                <X size={11} strokeWidth={1.8} /> withdraw
              </button>
            </li>
          ))}
          {(d?.invites || []).length === 0 && (
            <p className="font-data text-[11px] text-zinc-600">No invites outstanding.</p>
          )}
        </ul>
      </div>

      <p className="font-data text-[10px] leading-relaxed text-zinc-700">
        You are signed in as {d?.you || "—"} and cannot revoke yourself. Addresses listed in
        OPERATOR_EMAILS are granted by the environment and must be changed there.
      </p>

      {msg && (
        <p data-testid="operator-note" className="font-data text-[11px] text-zinc-400">
          — {msg}
        </p>
      )}

      <Housekeeping />
    </div>
  );
};

/** Clear QA fixtures and throwaway accounts out of whichever database this instance
 *  is attached to. The live site is attached to the live database, so this is how a
 *  published instance gets clean — preview and production do not share data. */
const Housekeeping = () => {
  const [plan, setPlan] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const look = async () => {
    setErr("");
    setResult(null);
    try {
      setPlan(await fetchPurgePlan());
    } catch (e) {
      setErr(e?.response?.data?.detail || "could not read the database");
    }
  };

  const purge = async () => {
    setBusy(true);
    setErr("");
    try {
      setResult(await purgeTestData(confirm));
      setConfirm("");
      setPlan(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "nothing was deleted");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="housekeeping"
      className="border border-[#b4232a] bg-[rgba(180,35,42,0.04)] px-5 py-6"
    >
      <p className="flex items-center gap-2 font-data text-[10px] uppercase tracking-[0.22em] text-[#ff6b6b]">
        <Trash2 size={11} strokeWidth={1.7} /> Danger zone · clear out test data
      </p>
      <p className="mt-2 max-w-2xl text-xs leading-relaxed text-zinc-500">
        Removes QA fixtures and throwaway accounts from the database this instance is attached to —
        so running it here cleans <em>this</em> environment, and running it on the live site cleans
        the live one. Real accounts and your own are never touched. Look first; nothing is deleted
        until you type the phrase.
      </p>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button data-testid="purge-plan-btn" onClick={look} className="btn-danger !py-3">
          Show me what would go
        </button>
      </div>

      {plan && (
        <div data-testid="purge-plan" className="mt-5 border border-[var(--line)] px-5 py-5">
          <p className="font-data text-[11px] text-zinc-200">
            {plan.accounts_to_delete.length} account
            {plan.accounts_to_delete.length === 1 ? "" : "s"} would be deleted from{" "}
            {plan.database}, with {plan.counts.messages} transcript turns and{" "}
            {plan.counts.sessions} engagement{plan.counts.sessions === 1 ? "" : "s"}.
          </p>
          <ul className="mt-4 space-y-1.5">
            {plan.accounts_to_delete.map((a) => (
              <li key={a.user_id} className="font-data text-[11px] text-zinc-400">
                — {a.email || a.user_id}
                {a.name ? ` · ${a.name}` : ""}
              </li>
            ))}
            {plan.accounts_to_delete.length === 0 && (
              <li className="font-data text-[11px] text-zinc-500">
                Nothing matches. This database is already clean.
              </li>
            )}
          </ul>
          {plan.accounts_kept.length > 0 && (
            <>
              <p className="label mt-5">Kept</p>
              <ul className="mt-2 space-y-1.5">
                {plan.accounts_kept.map((a) => (
                  <li key={a.user_id} className="font-data text-[11px] text-zinc-300">
                    — {a.email || a.user_id}
                    {a.is_operator ? " · operator" : ""}
                  </li>
                ))}
              </ul>
            </>
          )}
          <p className="mt-5 font-data text-[10px] leading-relaxed text-zinc-600">
            Also cleared: {plan.counts.password_resets} password-reset tokens and{" "}
            {plan.counts.login_attempts} login-attempt records. Invitations spent by a deleted
            tester are released for re-use rather than deleted.
          </p>

          {plan.accounts_to_delete.length > 0 && (
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <input
                data-testid="purge-confirm-input"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder={plan.confirm_with}
                className="min-w-[16rem] border border-[var(--line-hi)] bg-[#0a0a0d] px-4 py-3 font-data text-[12px] tracking-[0.12em] text-zinc-100 outline-none placeholder:text-zinc-700"
              />
              <button
                data-testid="purge-confirm-btn"
                disabled={busy || confirm.trim() !== plan.confirm_with}
                onClick={purge}
                className="btn-danger !py-3"
              >
                {busy ? "Deleting" : "Delete it permanently"}
              </button>
            </div>
          )}
        </div>
      )}

      {result && (
        <div data-testid="purge-result" className="mt-5 border border-zinc-600 px-5 py-5">
          <p className="font-data text-[11px] text-zinc-200">
            Deleted {result.deleted.users} accounts, {result.deleted.sessions} engagements and{" "}
            {result.deleted.messages} turns from {result.database}.
          </p>
          <p className="mt-3 font-data text-[10px] leading-relaxed text-zinc-500">
            {result.remaining.users} account{result.remaining.users === 1 ? "" : "s"} and{" "}
            {result.remaining.messages} turns remain.
            {result.deleted.invitations_released
              ? ` ${result.deleted.invitations_released} invitation(s) released for re-use.`
              : ""}
          </p>
        </div>
      )}

      {err && (
        <p data-testid="purge-error" className="mt-4 font-data text-[11px] text-[#ff6b6b]">
          — {err}
        </p>
      )}
    </div>
  );
};

export default Operators;
